import { ApiError, UnauthorizedError, isFieldErrorBody } from './errors'

/**
 * Fetch wrapper for both `/admin_api/v1/*` (Keitaro-compatible) and `/api/ui/*` (UI-only).
 * The SPA authenticates with a session cookie + `X-CSRF-Token` header (never `Api-Key`,
 * which is reserved for external integrations) — Plan 2A §1 "Auth".
 */

let csrfToken = ''
export function setCsrfToken(token: string) {
  csrfToken = token
}
export function getCsrfToken() {
  return csrfToken
}

export type UnauthorizedHandler = () => void
let onUnauthorized: UnauthorizedHandler | null = null
export function setUnauthorizedHandler(fn: UnauthorizedHandler | null) {
  onUnauthorized = fn
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  /** Skip the global 401 -> redirect handler (used by the login/me calls themselves). */
  skipAuthRedirect?: boolean
  signal?: AbortSignal
}

function buildQuery(query?: RequestOptions['query']): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    params.set(k, String(v))
  }
  const s = params.toString()
  return s ? `?${s}` : ''
}

async function parseErrorBody(
  res: Response,
): Promise<{ message: string; fields?: Record<string, string[]> }> {
  const text = await res.text()
  if (!text) return { message: res.statusText || `HTTP ${res.status}` }
  try {
    const json = JSON.parse(text)
    if (json && typeof json === 'object') {
      if (typeof json.error === 'string') return { message: json.error }
      if (isFieldErrorBody(json)) {
        const first = Object.values(json)[0]
        return {
          message: Array.isArray(first) ? String(first[0]) : 'Validation error',
          fields: json,
        }
      }
    }
    return { message: text }
  } catch {
    // Plain-text 404 bodies, e.g. `Traffic\Model\Campaign #1 not found`.
    return { message: text }
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET'
  const headers: Record<string, string> = { Accept: 'application/json' }
  const init: RequestInit = {
    method,
    headers,
    credentials: 'include',
    signal: opts.signal,
  }
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    init.body = JSON.stringify(opts.body)
  }
  if (method !== 'GET' && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken
  }

  const res = await fetch(`${path}${buildQuery(opts.query)}`, init)

  if (res.status === 401) {
    if (!opts.skipAuthRedirect && onUnauthorized) onUnauthorized()
    throw new UnauthorizedError()
  }
  if (!res.ok) {
    const { message, fields } = await parseErrorBody(res)
    throw new ApiError(message, res.status, { fields })
  }
  if (res.status === 200 && res.headers.get('content-length') === '0') {
    return undefined as T
  }
  const text = await res.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

export const http = {
  get: <T>(path: string, query?: RequestOptions['query'], opts: RequestOptions = {}) =>
    request<T>(path, { ...opts, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts: RequestOptions = {}) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
}
